import { supabase } from "@/integrations/supabase/client";
import type { Account } from "@/hooks/useAccounts";
import type { AnyValue } from "@/lib/rowTypes";

/**
 * Apply Scam Defense simulator consequences to the learner's existing
 * HeirWise accounts. Writes are:
 *   - idempotent (one txn per run_id + scenario_id + kind)
 *   - traceable (metadata.source = scam_defense_simulator)
 *   - clamped (cannot drive balance below 0)
 *   - committed only at end-of-run (call from completion handler)
 *
 * Never touches real-world banking. Real_money writes go through the
 * same RLS-scoped tables (`accounts`, `account_transactions`) already
 * used everywhere else in HeirWise.
 */

export type ScamConsequenceKind =
  | "simulated_scam_loss"
  | "simulated_scam_loss_avoided";

export interface ScamConsequenceWrite {
  runId: string;
  scenarioId: string;
  account: Account;
  amount: number; // always positive; sign comes from kind
  description: string;
  kind: ScamConsequenceKind;
}

export interface ScamConsequenceResult {
  ok: boolean;
  applied: boolean; // false if duplicate / clamped / no-op
  newBalance?: number;
  error?: string;
}

const DUP_LOOKBACK = 50;

async function alreadyApplied(
  userId: string,
  runId: string,
  scenarioId: string,
  kind: ScamConsequenceKind,
): Promise<boolean> {
  const { data } = await supabase
    .from("account_transactions")
    .select("id, metadata")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(DUP_LOOKBACK);
  const rows = (data as Array<{ metadata?: Record<string, AnyValue> }> | null) ?? [];
  return rows.some((r) => {
    const m = r.metadata ?? {};
    return (
      m.source === "scam_defense_simulator" &&
      m.run_id === runId &&
      m.scenario_id === scenarioId &&
      m.kind === kind
    );
  });
}

export async function applyScamConsequence(
  w: ScamConsequenceWrite,
): Promise<ScamConsequenceResult> {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id;
    if (!userId) return { ok: false, applied: false, error: "not_authenticated" };

    if (await alreadyApplied(userId, w.runId, w.scenarioId, w.kind)) {
      return { ok: true, applied: false };
    }

    // Compute new balance with floor at 0.
    const isLoss = w.kind === "simulated_scam_loss";
    const signed = isLoss ? -Math.abs(w.amount) : Math.abs(w.amount);
    const proposed = (w.account.balance ?? 0) + signed;
    const newBalance = isLoss ? Math.max(0, proposed) : proposed;
    const realDelta = newBalance - (w.account.balance ?? 0);

    // Skip the ledger row if a loss got fully clamped to 0 — nothing changed.
    if (isLoss && realDelta === 0) return { ok: true, applied: false };

    const txn = {
      user_id: userId,
      transaction_type: w.kind,
      amount: realDelta,
      description: w.description,
      source_account_id: isLoss ? w.account.id : null,
      destination_account_id: !isLoss ? w.account.id : null,
      metadata: {
        source: "scam_defense_simulator",
        run_id: w.runId,
        scenario_id: w.scenarioId,
        kind: w.kind,
        account_type: w.account.account_type,
        simulated: true,
      },
    };

    const { error: txnErr } = await supabase
      .from("account_transactions")
      .insert(txn as AnyValue);
    if (txnErr) return { ok: false, applied: false, error: txnErr.message };

    if (isLoss) {
      const { error: updErr } = await supabase
        .from("accounts")
        .update({ balance: newBalance, updated_at: new Date().toISOString() })
        .eq("id", w.account.id)
        .eq("user_id", userId);
      if (updErr) return { ok: false, applied: true, error: updErr.message, newBalance };
    }
    // 'loss_avoided' is a journaling-only entry — balance untouched.

    return { ok: true, applied: true, newBalance };
  } catch (err) {
    return { ok: false, applied: false, error: (err as Error).message };
  }
}